import { capitalizeFirstLetter } from "$utils/string";
import { sampleLibrary} from "../sampleLibrary";

const DIATONIC_MAJOR = [0, 2, 4, 5, 7, 9, 11];
const SCALES = {
    major: "1P 2M 3M 4P 5P 6M 7M",
    minor: "1P 2M 3m 4P 5P 6m 7m",
    wholetone: "1P 2M 3M 4A 5A 6A",

    locrian: "1P 2m 3m 4P 5d 6m 7m",

    "harmonic minor": "1P 2M 3m 4P 5P 6m 7M",
}

export const getPCNumber = (pcName) => {
    return {
        'c': 0, 
        'c#': 1, 'db': 1,
        'd': 2, 
        'd#': 3, 'eb': 3,
        'e': 4, 
        'f': 5, 
        'f#': 6, 'gb': 6,
        'g': 7, 
        'g#': 8, 'ab': 8,
        'a': 9, 
        'a#': 10, 'bb': 10,
        'b': 11
    }[pcName.toLowerCase()];
}

const wrappedPCNumber = (pcNumber) => {
    pcNumber = pcNumber % 12;
    if (pcNumber < 0) {
        return pcNumber + 12;
    }
    if (pcNumber >= 12) {
        return pcNumber - 12;
    }
    return pcNumber;
}

const getPCName = (pcNumber) => {
    return {
        0: 'C',
        1: 'C#',
        2: 'D',
        3: 'Eb',
        4: 'E',
        5: 'F',
        6: 'F#',
        7: 'G',
        8: 'Ab',
        9: 'A',
        10: 'Bb',
        11: 'B'
    }[pcNumber];
}
export const getNoteName = (pitch, octaveAdjustment=-1) => {
    const octaveNumber = ((pitch/12)|0)+octaveAdjustment;
    const letter = getPCName(pitch%12);
    return letter + octaveNumber;
}


export const getScaleRootAndType = (scaleName) => {
    const [root, type] = scaleName.split(":");
    return {root, type};
}

const makeScaleName = (pcName, scaleType) => {
    return `${pcName}:${scaleType}`;
}

export const getScaleDisplayName = (scale) => {
    const {root, type} = getScaleRootAndType(scale.toLowerCase());
    const rootDisplayName = capitalizeFirstLetter(root);
    let typeDisplayName = " " + type;
    if (type === "major") typeDisplayName = ""
    else if (type === "minor") typeDisplayName = "-"
    return rootDisplayName + typeDisplayName;
}

export const isScaleCompatibleWith = (scale1, scale2) => {
    if (scale1 == null || scale2 == null) {
        return false;
    }
    const scale1PC = getPCsFromScaleName(scale1);
    const scale2PC = getPCsFromScaleName(scale2);
    return scale1PC.isSubsetOf(scale2PC);
}

export const getTranspositionForScaleToMatchTonality = (scale, tonality) => {
    if (scale === tonality) return 0;
    const {root: scaleRoot, type: scaleType} = getScaleRootAndType(scale);
    const {root: tonalityRoot, type: tonalityType} = getScaleRootAndType(tonality);
    const majorTonalityOffsetForScaleType = {
        major: 0,
        minor: -3,
        "harmonic minor": -3,
        locrian: -1,
    };
    let offset = majorTonalityOffsetForScaleType[scaleType];
    if (offset != null && tonalityType === "minor") offset +=3;
    offset ??= 0;

    const scaleRootPC = getPCNumber(scaleRoot);
    const tonalityRootPC = getPCNumber(tonalityRoot);
    offset += tonalityRootPC - scaleRootPC;
    offset = (offset % 12 + 12) % 12;
    if (offset > 6) offset = offset - 12;
    return offset;
}

export const transposeScale = (scaleName, semitones) => {
    if (scaleName === undefined) {
        return undefined;
    }
    const {root: pcName, type} = getScaleRootAndType(scaleName);
    let newPCNumber = wrappedPCNumber(getPCNumber(pcName) + semitones);
    return makeScaleName(getPCName(newPCNumber), type);
}


export const getScaleForSampleName = (sampleName="") => {
    if (sampleName === "") return undefined;

    const sound = sampleLibrary.sampler.find(sound => sound.name.toLowerCase() === sampleName.toLowerCase());
    if (sound) return sound.scale;
}

// returns an array with the index corresponding to the position on the scale
// and the number corresponding to the Pitch Class
export const getPCsFromScaleName = (scale) => {
    const { root, type } = getScaleRootAndType(scale);
    const rootPC = getPCNumber(root);
    return new Set(buildScaleFromRootPitch(rootPC, type).map(pitch => pitch % 12));
};

export const buildScaleFromRootPitch = (rootPitch, scaleType="major") => {
    return [0, ...SCALES[scaleType].split(" ").map(diatonicInterval => diatonicToChromatic(diatonicInterval))]
        .map(interval => rootPitch + interval);
}

const ascii = a => a.charCodeAt(0);

const getWhiteNote = noteClass => noteClass.slice(0,1) + noteClass.slice(1).replaceAll('#', '').replaceAll('b', '');

const getScaleDegreeOfNoteClass = (noteClass, rootNoteClass) => {
    const rootWhiteAscii = ascii(getWhiteNote(rootNoteClass));
    const noteWhiteAscii = ascii(getWhiteNote(noteClass));
    return noteWhiteAscii - rootWhiteAscii + (rootWhiteAscii > noteWhiteAscii ? 7 : 0);
}

const separateNoteClassAndOctave = compoundNote => {
    return [compoundNote.slice(0, -1), compoundNote.slice(-1)]
}

const noteStrToMidi = (noteStr) => {
    [noteClass, octave] = separateNoteClassAndOctave(noteStr);
    return getPCNumber(noteClass) + (octave + 1) * 12;
}

export const calcDiatonicInterval = (note1, note2) => {
    const semitoneDiff = note2 - note1;
    const directionMultiplier = semitoneDiff < 0 ? -1 : 1; // for when it's downwards
    const absModSemitoneDiff = Math.abs(semitoneDiff) % 12;
    const octaveDiff = Math.floor(Math.abs(semitoneDiff) / 12);
    const [absModQuantity, quality] = {
        0: [0, 'P'],
        1: [1, 'm'], 2: [1, 'M'],
        3: [2, 'm'], 4: [2, 'M'],
        5: [3, 'P'],
        6: [4, 'd'], 7: [4, 'P'],
        8: [5, 'm'], 9: [5, 'M'],
        10: [6, 'm'], 11: [6, 'M']
    }[absModSemitoneDiff];
    const quantity = directionMultiplier * (absModQuantity + 7 * octaveDiff);
    return [quantity, quality]
}

const diatonicToChromatic = (diatonicInterval) => {
    const quality = diatonicInterval.slice(-1);
    const number = Number(diatonicInterval.slice(0, -1));
    const tonalIntervalQualities = {
        'A': 1,
        'P': 0,
        'd': -1
    }
    const modalIntervalQualities = {
        'A': 1,
        'M': 0,
        'm': -1,
        'd': -2
    }
    const isTonal = number === 1 || number === 4 || number === 5;
    offset = (isTonal ? tonalIntervalQualities : modalIntervalQualities)[quality];
    const majorOrJust = DIATONIC_MAJOR[number - 1];
    const chromatic = majorOrJust + offset;
    return chromatic;
}

export const getNextPitchClassAfterPitch = (pitchClass, midiPitch) => {
    return midiPitch - (midiPitch % 12) + pitchClass + (midiPitch % 12 <= pitchClass ? 0 : 12);
}